<template>
  <div id="drivers-map">
    <v-snackbar
      v-model="isSnackbarVisible"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
      auto-height
    >
      <v-layout align-center pr-4>
        <v-icon class="pr-3" dark large>
          {{ snackbar.icon == 'success' ? icons.mdiCheckBold : icons.mdiAlertCircle }}
        </v-icon>
        <v-layout column>
          <div>
            <strong>{{ snackbar.title }}</strong>
          </div>
          <div>{{ snackbar.text }}</div>
        </v-layout>
      </v-layout>
    </v-snackbar>

    <h2 class="m-2">CONDUCTORES EN TIEMPO REAL</h2>

    <v-card class="mb-3">
      <v-card-text class="py-2">
        <div class="d-flex align-center flex-wrap">
          <span class="text-sm font-weight-medium mr-3">Estado:</span>

          <v-chip small color="green" text-color="white" class="mr-2">
            <v-icon small left>{{ icons.mdiMapMarker }}</v-icon>
            Sin órdenes: {{ countSinOrdenes }}
          </v-chip>
          <v-chip small color="blue" text-color="white" class="mr-2">
            <v-icon small left>{{ icons.mdiTruck }}</v-icon>
            En proceso: {{ countEnProceso }}
          </v-chip>
          <v-chip small color="orange" text-color="white" class="mr-2">
            <v-icon small left>{{ icons.mdiClock }}</v-icon>
            En espera: {{ countEnEspera }}
          </v-chip>
          <v-chip small color="grey" text-color="white" class="mr-2">
            <v-icon small left>{{ icons.mdiCloseCircle }}</v-icon>
            Desconectado: {{ countDesconectado }}
          </v-chip>

          <v-spacer></v-spacer>

          <v-chip small :color="socketConnected ? 'green' : 'red'" text-color="white" class="mr-2">
            <v-icon small left>{{ socketConnected ? icons.mdiCheckBold : icons.mdiCloseCircle }}</v-icon>
            {{ socketConnected ? 'Tiempo real' : 'Sin socket' }}
          </v-chip>

          <v-btn small @click="refresh" :loading="loading" color="primary">
            <v-icon small left>{{ icons.mdiReload }}</v-icon>
            Refrescar
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-card>
      <!-- Controles sobre el mapa -->
      <div style="display: flex; align-items: center; flex-wrap: wrap; padding: 8px 12px; background: #f5f5f5; border-bottom: 1px solid #e0e0e0; gap: 8px;">
        <span style="font-size: 13px; font-weight: 600; color: #555;">Estilo del mapa:</span>
        <button
          v-for="style in mapStyles"
          :key="style.id"
          @click="setMapStyle(style.id)"
          :disabled="changingStyle"
          :style="'padding: 6px 12px; border: 1px solid ' + (mapStyle === style.id ? '#1976d2' : '#ccc') + '; background: ' + (mapStyle === style.id ? '#1976d2' : 'white') + '; color: ' + (mapStyle === style.id ? 'white' : '#333') + '; border-radius: 4px; cursor: pointer; font-size: 12px; font-weight: 500;'"
        >
          {{ style.label }}
        </button>

        <span style="flex: 1;"></span>

        <button
          v-if="selectedDriverId"
          @click="toggleOrderPoints"
          :style="'padding: 6px 12px; border: 1px solid ' + (showOrderPoints ? '#ff9800' : '#ccc') + '; background: ' + (showOrderPoints ? '#ff9800' : 'white') + '; color: ' + (showOrderPoints ? 'white' : '#333') + '; border-radius: 4px; cursor: pointer; font-size: 12px; font-weight: 500;'"
          :title="showOrderPoints ? 'Ocultar puntos de la orden' : 'Mostrar puntos de la orden'"
        >
          {{ showOrderPoints ? 'Ocultar puntos' : 'Mostrar puntos' }}
        </button>

        <button
          v-if="selectedDriverId"
          @click="fitToSelectedDriver"
          style="padding: 6px 12px; border: 1px solid #4caf50; background: white; color: #4caf50; border-radius: 4px; cursor: pointer; font-size: 12px; font-weight: 500;"
        >
          Centrar en driver
        </button>
      </div>

      <div v-if="showMap" id="map" style="height: 600px; position: relative;">
        <MglMap
          :key="mapKey"
          :center.sync="center"
          :access-token="accessToken"
          :map-style="initialMapStyle"
          :zoom="zoom"
          @load="onMapLoad"
        />
      </div>
    </v-card>

    <v-card class="mt-3">
      <v-card-text>
        <div class="d-flex align-center mb-2">
          <h3>Listado de conductores</h3>
          <v-spacer></v-spacer>
          <v-chip v-if="selectedDriverId" small color="primary" text-color="white" class="mr-2">
            Filtrando: {{ getSelectedDriverName() }}
            <v-icon small right @click="clearSelection">mdi-close</v-icon>
          </v-chip>
          <v-btn v-if="selectedDriverId" small color="secondary" @click="clearSelection">
            <v-icon small left>{{ icons.mdiMapMarker }}</v-icon>
            Mostrar todos
          </v-btn>
        </div>
        <table style="width: 100%; border-collapse: collapse; font-size: 14px; background: white;">
          <thead>
            <tr style="background: #f5f5f5; border-bottom: 2px solid #e0e0e0;">
              <th style="padding: 10px; text-align: left; width: 50px;">ID</th>
              <th style="padding: 10px; text-align: left;">Nombre</th>
              <th style="padding: 10px; text-align: left;">Estado</th>
              <th style="padding: 10px; text-align: center; width: 100px;">Orden actual</th>
              <th style="padding: 10px; text-align: center; width: 80px;">Órdenes pendientes</th>
              <th style="padding: 10px; text-align: left; width: 180px;">Última actualización</th>
              <th style="padding: 10px; text-align: center; width: 50px;">Ver</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in driversTable"
              :key="item.id"
              :style="getRowStyle(item)"
            >
              <td :style="getCellStyle(item, 'center')" style="padding: 10px;">{{ item.id }}</td>
              <td :style="getCellStyle(item, 'left')" style="padding: 10px;">
                <strong :style="item.estadoCls === 'desconectado' ? 'color: #9e9e9e;' : ''">{{ item.nombre }}</strong>
              </td>
              <td :style="getCellStyle(item, 'left')" style="padding: 10px;">
                <span
                  v-if="item.orden_actual && Number(item.orden_actual) > 0"
                  @click.stop="openOrderDialog(item.orden_actual)"
                  :style="'display: inline-block; padding: 4px 12px; border-radius: 12px; color: white; font-size: 12px; font-weight: 600; background: ' + item.estadoColor + '; cursor: pointer; user-select: none;'"
                  :title="'Click para ver detalles de la orden #' + item.orden_actual"
                >
                  {{ item.estadoLabel }}
                </span>
                <span
                  v-else
                  :style="'display: inline-block; padding: 4px 12px; border-radius: 12px; color: white; font-size: 12px; font-weight: 600; background: ' + item.estadoColor + ';'"
                >
                  {{ item.estadoLabel }}
                </span>
              </td>
              <td :style="getCellStyle(item, 'center')" style="padding: 10px;">
                <span
                  v-if="item.orden_actual && Number(item.orden_actual) > 0"
                  @click.stop="openOrderDialog(item.orden_actual)"
                  style="display: inline-block; padding: 4px 10px; border-radius: 10px; color: white; font-size: 12px; font-weight: 600; background: #2196f3; cursor: pointer;"
                >
                  #{{ item.orden_actual }}
                </span>
                <span v-else style="color: #9e9e9e;">-</span>
              </td>
              <td :style="getCellStyle(item, 'center')" style="padding: 10px;">{{ item.ordenes_pendientes }}</td>
              <td :style="getCellStyle(item, 'left')" style="padding: 10px;">{{ item.ultActLabel || '-' }}</td>
              <td :style="getCellStyle(item, 'center')" style="padding: 10px;">
                <button
                  @click.stop="selectDriver(item)"
                  style="background: none; border: none; color: #2196f3; cursor: pointer; padding: 4px 8px;"
                >
                  <span style="font-size: 18px;">📍</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </v-card-text>
    </v-card>

    <!-- Modal de información de la orden -->
    <div v-if="orderDialog" @click.self="closeOrderDialog" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); z-index: 9999; display: flex; align-items: center; justify-content: center;">
      <div style="background: white; border-radius: 8px; width: 90%; max-width: 800px; max-height: 90vh; overflow-y: auto; box-shadow: 0 10px 40px rgba(0,0,0,0.3);">
        <div style="background: #1976d2; color: white; padding: 16px 20px; display: flex; align-items: center; border-radius: 8px 8px 0 0;">
          <span style="font-size: 18px; font-weight: 600;">Orden #{{ orderInfo && orderInfo.id }}</span>
          <span v-if="orderInfo && orderInfo.estado !== undefined" :style="'margin-left: 12px; padding: 4px 10px; border-radius: 12px; font-size: 12px; font-weight: 600; background: ' + ordenEstadoColor + ';'">
            {{ ordenEstadoLabel }}
          </span>
          <span style="flex: 1;"></span>
          <button @click="closeOrderDialog" style="background: none; border: none; color: white; font-size: 24px; cursor: pointer; line-height: 1;">×</button>
        </div>

        <div v-if="orderLoading" style="text-align: center; padding: 40px;">
          <div style="font-size: 14px; color: #666;">Cargando información de la orden...</div>
        </div>

        <div v-else-if="orderInfo" style="padding: 20px;">
          <div style="display: grid; grid-template-columns: 140px 1fr; gap: 10px 16px; font-size: 14px;">
            <div style="color: #888; font-weight: 600;">Cliente</div>
            <div>{{ orderInfo.nombre || '-' }}</div>

            <div style="color: #888; font-weight: 600;">Conductor asignado</div>
            <div>
              <span v-if="driverForOrder">{{ driverForOrder.nombre }} (ID {{ driverForOrder.id }})</span>
              <span v-else>ID {{ orderInfo.conductor_id || '-' }}</span>
            </div>

            <div style="color: #888; font-weight: 600;">Total</div>
            <div>C$ {{ Number(orderInfo.total || 0).toFixed(2) }}</div>

            <div style="color: #888; font-weight: 600;">Distancia total</div>
            <div>{{ orderInfo.distancia_viaje || '-' }} km</div>

            <div style="color: #888; font-weight: 600;">Tiempo estimado</div>
            <div>{{ orderInfo.tiempo_entrega || '-' }} min</div>

            <div style="color: #888; font-weight: 600;">Método de pago</div>
            <div>{{ orderInfo.metodo_pago || '-' }}</div>

            <div style="color: #888; font-weight: 600;">Fecha de la orden</div>
            <div>{{ orderInfo.fecha_orden || '-' }}</div>
          </div>

          <hr style="margin: 20px 0; border: none; border-top: 1px solid #e0e0e0;">

          <h4 style="margin: 0 0 12px 0; font-size: 14px; color: #333;">Puntos de la orden</h4>
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <thead>
              <tr style="background: #f5f5f5;">
                <th style="padding: 8px; text-align: left; width: 30px;">#</th>
                <th style="padding: 8px; text-align: left;">Nombre</th>
                <th style="padding: 8px; text-align: left;">Dirección</th>
                <th style="padding: 8px; text-align: left;">Coordenadas</th>
                <th style="padding: 8px; text-align: right; width: 70px;">Distancia</th>
                <th style="padding: 8px; text-align: right; width: 70px;">Tiempo</th>
                <th style="padding: 8px; text-align: center; width: 90px;">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in orderPuntos" :key="p.id" style="border-bottom: 1px solid #eee;">
                <td style="padding: 8px;">{{ p.id }}</td>
                <td style="padding: 8px;">{{ p.nombre }}</td>
                <td style="padding: 8px;">{{ p.direccion }}</td>
                <td style="padding: 8px; font-family: monospace; font-size: 11px;">{{ p.coordenadas_entrega }}</td>
                <td style="padding: 8px; text-align: right; font-family: monospace;">{{ p.distancia_viaje }}{{ p.distancia_viaje && p.distancia_viaje !== '-' ? ' km' : '' }}</td>
                <td style="padding: 8px; text-align: right; font-family: monospace;">{{ p.tiempo_viaje }}{{ p.tiempo_viaje && p.tiempo_viaje !== '-' ? ' min' : '' }}</td>
                <td style="padding: 8px; text-align: center;">
                  <span :style="'display: inline-block; padding: 2px 8px; border-radius: 10px; color: white; font-size: 11px; font-weight: 600; background: ' + (p.estado ? '#4caf50' : '#9e9e9e') + ';'">
                    {{ p.estado ? 'Completado' : 'Pendiente' }}
                  </span>
                </td>
              </tr>
              <tr v-if="orderPuntos.length === 0">
                <td colspan="7" style="padding: 16px; text-align: center; color: #999;">No hay puntos registrados</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else style="padding: 20px; background: #ffebee; color: #c62828; border-radius: 4px; margin: 20px;">
          No se pudo cargar la información de la orden.
        </div>

        <div style="padding: 16px 20px; border-top: 1px solid #e0e0e0; display: flex; justify-content: flex-end; gap: 8px; background: #fafafa; border-radius: 0 0 8px 8px;">
          <button @click="closeOrderDialog" style="background: #e0e0e0; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 14px;">Cerrar</button>
          <button v-if="orderInfo && orderInfo.id" @click="goToOrderMap(orderInfo.id)" style="background: #4caf50; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 14px;">
            Ver en mapa
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mdiAlertCircle,
  mdiCheckBold,
  mdiClock,
  mdiCloseCircle,
  mdiMapMarker,
  mdiReload,
  mdiTruck,
} from '@mdi/js'

import {
  MglMap,
} from 'vue-mapbox'

import mapboxgl from 'mapbox-gl'
import io from 'socket.io-client'

export default {
  components: {
    MglMap,
  },
  data() {
    return {
      icons: {
        mdiAlertCircle,
        mdiCheckBold,
        mdiClock,
        mdiCloseCircle,
        mdiMapMarker,
        mdiReload,
        mdiTruck,
      },
      drivers: [],
      selectedDriverId: null,
      mapMarkers: {},
      orderPointMarkers: [],
      showOrderPoints: false,
      mapStyles: [
        { id: 'mapbox://styles/mapbox/streets-v11', label: 'Calles' },
        { id: 'mapbox://styles/mapbox/satellite-streets-v11', label: 'Satélite' },
        { id: 'mapbox://styles/mapbox/light-v10', label: 'Claro' },
        { id: 'mapbox://styles/mapbox/dark-v10', label: 'Oscuro' },
        { id: 'mapbox://styles/mapbox/outdoors-v11', label: 'Outdoor' },
        { id: 'mapbox://styles/mapbox/satellite-v9', label: 'Satélite puro' },
      ],
      changingStyle: false,
      loading: false,
      orderDialog: false,
      orderLoading: false,
      orderInfo: null,
      orderPuntos: [],
      orderDialogId: null,
      isSnackbarVisible: false,
      snackbar: {
        color: 'success',
        icon: 'check_circle',
        title: 'OK',
        text: '',
        timeout: 3000,
      },
      accessToken: 'pk.eyJ1IjoieWFxdWkiLCJhIjoiY200NTVsdDR1MDYwbzJrb2phODM0dzN5ZCJ9.oBzYf5xlVu_iQUc47GLh1A',
      initialMapStyle: 'mapbox://styles/mapbox/satellite-streets-v11',
      mapStyle: 'mapbox://styles/mapbox/satellite-streets-v11',
      center: [-86.108508886867, 11.912447380368315],
      zoom: 7,
      mapLoaded: false,
      isLoadingMap: false,
      showMap: true,
      mapKey: 0,
      Mymap: null,
      refreshInterval: null,
      socket: null,
      socketConnected: false,
      lastSocketUpdate: null,
      tableHeaders: [
        { text: 'ID', value: 'id', sortable: true },
        { text: 'Nombre', value: 'nombre', sortable: true },
        { text: 'Estado', value: 'estado', sortable: true },
        { text: 'Orden actual', value: 'orden_actual', sortable: true, align: 'center' },
        { text: 'Órdenes pendientes', value: 'ordenes_pendientes', sortable: true },
        { text: 'Última actualización', value: 'ult_actualizacion_coordenadas', sortable: true },
        { text: 'Ver', value: 'acciones', sortable: false, align: 'center' },
      ],
    }
  },
  computed: {
    driversMarkers() {
      return this.drivers
        .filter(d => {
          if (this.selectedDriverId && Number(d.id) !== Number(this.selectedDriverId)) return false
          const lat = parseFloat(d.lat_actual)
          const lng = parseFloat(d.lng_actual)
          if (isNaN(lat) || isNaN(lng)) return false
          // Filtrar coordenadas inválidas (0,0) o NaN
          if (lat === 0 && lng === 0) return false
          // Rango centroamericano: lat 7-18, lng -93 a -77
          // Cubre: Guatemala, Belice, Honduras, El Salvador, Nicaragua, Costa Rica, Panamá
          if (lat < 7 || lat > 18 || lng < -93 || lng > -77) return false
          return true
        })
        .map(d => {
          const estado = this.computeEstado(d)
          return {
            id: d.id,
            nombre: d.nombre,
            coordinates: [parseFloat(d.lng_actual), parseFloat(d.lat_actual)],
            estadoClass: estado.cls,
            estadoLabel: estado.label,
            ordenes_pendientes: d.ordenes_pendientes,
            ultActLabel: this.formatFecha(d.ult_actualizacion_coordenadas),
            icon: estado.icon,
          }
        })
    },
    driversTable() {
      return this.drivers.map(d => {
        const estado = this.computeEstado(d)
        return {
          id: d.id,
          nombre: d.nombre,
          estadoLabel: estado.label,
          estadoColor: estado.color,
          estadoCls: estado.cls,
          orden_actual: d.orden_actual,
          ordenes_pendientes: d.ordenes_pendientes,
          ult_actualizacion_coordenadas: d.ult_actualizacion_coordenadas,
          ultActLabel: this.formatFecha(d.ult_actualizacion_coordenadas),
        }
      })
    },
    countSinOrdenes() {
      return this.drivers.filter(d => this.computeEstado(d).cls === 'sin-ordenes').length
    },
    countEnEspera() {
      return this.drivers.filter(d => this.computeEstado(d).cls === 'en-espera').length
    },
    countEnProceso() {
      return this.drivers.filter(d => this.computeEstado(d).cls === 'en-proceso').length
    },
    countDesconectado() {
      return this.drivers.filter(d => this.computeEstado(d).cls === 'desconectado').length
    },
  },
  async mounted() {
    // Cargar estilo de mapa guardado
    try {
      let savedStyle = localStorage.getItem('driversMap_style')
      // Si el estilo guardado es v12 o v11 de estilos no soportados por mapbox-gl v2, usar default
      if (savedStyle && /-v12|-v11/.test(savedStyle) && /streets|outdoors|light|dark/.test(savedStyle)) {
        // streets-v11, outdoors-v11, light-v10, dark-v10 sí son válidos. v12 no.
        if (/-v12/.test(savedStyle)) {
          // Mapear v12 a v11 equivalente
          savedStyle = savedStyle.replace('-v12', '-v11')
        }
      }
      if (savedStyle) {
        this.mapStyle = savedStyle
        this.initialMapStyle = savedStyle
      }
    } catch (e) {}
    await this.refresh()
    this.connectSocket()
    // Polling de respaldo cada 60s (la actualización en tiempo real es por socket)
    this.refreshInterval = setInterval(() => {
      this.refresh(false)
    }, 60000)
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }
    if (this.socket) {
      try { this.socket.disconnect() } catch (e) {}
      this.socket = null
    }
    // Limpiar markers
    Object.values(this.mapMarkers).forEach(m => {
      try { m.remove() } catch (e) {}
    })
    this.mapMarkers = {}
    // Limpiar markers de puntos
    this.orderPointMarkers.forEach(m => {
      try { m.remove() } catch (e) {}
    })
    this.orderPointMarkers = []
    this.Mymap = null
  },
  methods: {
    computeEstado(d) {
      if (Number(d.conectado) !== 1) {
        return { cls: 'desconectado', label: 'Desconectado', color: 'grey', icon: this.icons.mdiCloseCircle }
      }
      // Prioridad: si tiene orden_actual > 0, está en curso
      if (Number(d.orden_actual) > 0) {
        const nroOrden = d.orden_actual
        const estado = d.estado_orden_actual != null ? d.estado_orden_actual : '?'
        return { cls: 'en-curso', label: 'En curso #' + nroOrden + ' (pto ' + estado + ')', color: 'blue', icon: this.icons.mdiTruck }
      }
      if (Number(d.ordenes_en_proceso) > 0) {
        const estado = d.estado_orden_actual != null ? d.estado_orden_actual : '?'
        return { cls: 'en-proceso', label: 'En proceso (pto ' + estado + ')', color: 'blue', icon: this.icons.mdiTruck }
      }
      if (Number(d.ordenes_pendientes) > 0) {
        return { cls: 'en-espera', label: 'En espera', color: 'orange', icon: this.icons.mdiClock }
      }
      return { cls: 'sin-ordenes', label: 'Sin órdenes', color: 'green', icon: this.icons.mdiTruck }
    },
    formatFecha(fecha) {
      if (!fecha) return '-'
      const s = String(fecha).trim()
      // Capturar exactamente YYYY-MM-DD HH:mm:ss desde la cadena, sin zona horaria.
      // Soporta: "2026-06-11 04:19:14", "2026-06-11T04:19:14", "2026-06-11T04:19:14.000Z"
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/)
      if (m) {
        return m[1] + '-' + m[2] + '-' + m[3] + ' ' + m[4] + ':' + m[5] + ':' + m[6]
      }
      return s
    },
    async refresh(showSnackbar = true) {
      try {
        this.loading = true
        const url = (this.$store && this.$store.state && this.$store.state.url ? this.$store.state.url : '') + 'usuarios/getDriversWithStatus'
        console.log('[DriversMap] GET', url)
        const res = await this.$store.dispatch('get', { path: 'usuarios/getDriversWithStatus' })
        console.log('[DriversMap] respuesta:', res)
        if (res && res.mensaje) {
          this.drivers = []
          this.loading = false
          this.isSnackbarVisible = true
          this.snackbar = {
            color: 'error',
            icon: 'error',
            title: 'Backend',
            text: res.mensaje,
            timeout: 4000,
          }
          return
        }
        this.drivers = Array.isArray(res) ? res : []
        this.loading = false
        this.renderMarkers()
        if (showSnackbar) {
          this.isSnackbarVisible = true
          this.snackbar = {
            color: this.drivers.length > 0 ? 'success' : 'warning',
            icon: this.drivers.length > 0 ? 'success' : 'info',
            title: this.drivers.length > 0 ? 'Actualizado' : 'Sin datos',
            text: this.drivers.length + ' conductores cargados desde ' + url,
            timeout: 2500,
          }
        }
      } catch (error) {
        this.loading = false
        this.isSnackbarVisible = true
        this.snackbar = {
          color: 'error',
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los conductores: ' + (error && error.message ? error.message : error),
          timeout: 4000,
        }
        console.error('[DriversMap] error refresh:', error)
      }
    },
    async onMapLoad(event) {
      console.log('📍 DriversMap onMapLoad', event)
      this.Mymap = event
      // En algunas versiones de vue-mapbox el evento es el map directo, en otras es el componente
      const mapInstance = (event && event.map) ? event.map : event
      console.log('📍 DriversMap mapInstance has setStyle?', typeof (mapInstance && mapInstance.setStyle))
      this._mapInstance = mapInstance
      // Render inicial
      this.$nextTick(() => {
        this.renderMarkers()
      })
    },
    estadoClassToColor(cls) {
      if (cls === 'sin-ordenes') return '#4caf50'
      if (cls === 'en-espera') return '#ff9800'
      if (cls === 'en-proceso') return '#2196f3'
      return '#9e9e9e'
    },
    estadoClassToLabel(cls) {
      if (cls === 'sin-ordenes') return 'Sin órdenes'
      if (cls === 'en-espera') return 'En espera'
      return 'Desconectado'
    },
    createMarkerElement(driver) {
      const el = document.createElement('div')
      el.className = 'driver-marker driver-' + driver.estadoClass
      el.style.cssText = 'width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.5); cursor: pointer; color: white; font-size: 16px;'
      el.style.backgroundColor = this.estadoClassToColor(driver.estadoClass)
      // icono de material-design-icons vía mapbox-gl no es trivial; uso texto
      el.innerHTML = '🚗'
      el.title = driver.nombre
      return el
    },
    renderMarkers() {
      if (!this.Mymap || !this.Mymap.map) {
        console.log('[DriversMap] renderMarkers: mapa no listo')
        return
      }
      const map = this.Mymap.map
      const desiredIds = new Set(this.driversMarkers.map(d => d.id))

      // Quitar markers que ya no están
      Object.keys(this.mapMarkers).forEach(idStr => {
        const id = Number(idStr)
        if (!desiredIds.has(id)) {
          try {
            this.mapMarkers[id].remove()
          } catch (e) {}
          delete this.mapMarkers[id]
        }
      })

      // Agregar o actualizar los markers visibles
      this.driversMarkers.forEach(d => {
        const lng = d.coordinates[0]
        const lat = d.coordinates[1]
        if (this.mapMarkers[d.id]) {
          // actualizar posición
          try {
            this.mapMarkers[d.id].setLngLat([lng, lat])
          } catch (e) {}
        } else {
          try {
            const el = this.createMarkerElement(d)
            const popup = new mapboxgl.Popup({ offset: 25, closeButton: true }).setHTML(
              '<div style="min-width: 180px">' +
                '<div style="font-size: 16px; font-weight: bold">' + d.nombre + '</div>' +
                '<div style="font-size: 12px; color: #666">ID: ' + d.id + '</div>' +
                '<div style="font-size: 13px; margin-top: 4px"><strong>Estado:</strong> ' + d.estadoLabel + '</div>' +
                '<div style="font-size: 13px"><strong>Órdenes pendientes:</strong> ' + d.ordenes_pendientes + '</div>' +
                '<div style="font-size: 12px; color: #666; margin-top: 4px">Última act.: ' + d.ultActLabel + '</div>' +
              '</div>'
            )
            const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
              .setLngLat([lng, lat])
              .setPopup(popup)
              .addTo(map)
            this.mapMarkers[d.id] = marker
          } catch (e) {
            console.error('[DriversMap] error creando marker para driver', d.id, e)
          }
        }
      })
      console.log('[DriversMap] markers renderizados:', Object.keys(this.mapMarkers).length, 'de', this.drivers.length, 'conductores')
    },
    onRowClick(item) {
      this.selectDriver(item)
    },
    selectDriver(item) {
      const driver = this.drivers.find(d => Number(d.id) === Number(item.id))
      if (!driver) {
        console.warn('[DriversMap] selectDriver: no se encontró driver id=' + item.id)
        return
      }
      this.selectedDriverId = Number(driver.id)
      const lat = parseFloat(driver.lat_actual)
      const lng = parseFloat(driver.lng_actual)
      if (!isNaN(lat) && !isNaN(lng) && this.Mymap && this.Mymap.map) {
        this.Mymap.map.flyTo({
          center: [lng, lat],
          zoom: 15,
          speed: 1.5,
        })
      }
      this.renderMarkers()
      this.isSnackbarVisible = true
      this.snackbar = {
        color: 'primary',
        icon: 'info',
        title: 'Filtrando',
        text: 'Mostrando solo a ' + driver.nombre,
        timeout: 2000,
      }
      console.log('[DriversMap] seleccionado driver id=' + driver.id + ' nombre=' + driver.nombre)
    },
    clearSelection() {
      this.selectedDriverId = null
      this.clearOrderPoints()
      this.renderMarkers()
      console.log('[DriversMap] filtro limpiado, mostrando todos')
    },
    setMapStyle(styleId) {
      if (this.mapStyle === styleId) return
      this.changingStyle = true
      this.mapStyle = styleId
      localStorage.setItem('driversMap_style', styleId)
      console.log('[DriversMap] estilo cambiado a', styleId)
      // Intentar obtener la instancia del mapa de varias formas
      const map = (this.Mymap && this.Mymap.map) || this._mapInstance || this.Mymap
      console.log('[DriversMap] map disponible?', !!map, 'setStyle?', typeof (map && map.setStyle))
      if (map && typeof map.setStyle === 'function') {
        // Limpiar markers existentes: mapbox-gl los elimina al cambiar el estilo
        Object.values(this.mapMarkers).forEach(m => {
          try { m.remove() } catch (e) {}
        })
        this.mapMarkers = {}
        // Limpiar también markers de puntos de la orden
        this.orderPointMarkers.forEach(m => {
          try { m.remove() } catch (e) {}
        })
        this.orderPointMarkers = []
        try {
          map.setStyle(styleId)
          // Cuando el nuevo estilo termine de cargar, re-renderizar markers
          const onStyleLoad = () => {
            map.off('style.load', onStyleLoad)
            console.log('[DriversMap] nuevo estilo cargado, re-renderizando markers')
            this.renderMarkers()
            if (this.selectedOrder) this.loadOrderPoints(this.selectedOrder)
          }
          map.on('style.load', onStyleLoad)
        } catch (e) {
          console.warn('[DriversMap] setStyle error', e)
        }
      } else {
        console.warn('[DriversMap] no se pudo obtener instancia del mapa, re-creando componente')
        // Como fallback, forzar re-mount del componente
        this.showMap = false
        this.$nextTick(() => { this.showMap = true })
      }
      setTimeout(() => { this.changingStyle = false }, 1500)
    },
    fitToSelectedDriver() {
      if (!this.Mymap || !this.Mymap.map || !this.selectedDriverId) return
      const driver = this.drivers.find(d => Number(d.id) === Number(this.selectedDriverId))
      if (!driver) return
      const lat = parseFloat(driver.lat_actual)
      const lng = parseFloat(driver.lng_actual)
      if (isNaN(lat) || isNaN(lng)) return
      this.Mymap.map.flyTo({ center: [lng, lat], zoom: 16, speed: 1.5 })
    },
    toggleOrderPoints() {
      if (this.showOrderPoints) {
        this.clearOrderPoints()
      } else {
        this.loadOrderPoints()
      }
    },
    clearOrderPoints() {
      this.orderPointMarkers.forEach(m => {
        try { m.remove() } catch (e) {}
      })
      this.orderPointMarkers = []
      this.showOrderPoints = false
    },
    async loadOrderPoints() {
      if (!this.selectedDriverId) {
        this.isSnackbarVisible = true
        this.snackbar = {
          color: 'warning',
          icon: 'info',
          title: 'Selecciona un driver',
          text: 'Selecciona primero un driver con orden activa.',
          timeout: 2500,
        }
        return
      }
      const driver = this.drivers.find(d => Number(d.id) === Number(this.selectedDriverId))
      if (!driver || !driver.orden_actual) return

      try {
        const res = await this.$store.dispatch('get', { path: 'ordenes/getById/' + driver.orden_actual })
        const orden = Array.isArray(res) ? res[0] : res
        if (!orden) {
          this.isSnackbarVisible = true
          this.snackbar = { color: 'error', icon: 'error', title: 'Error', text: 'No se pudo cargar la orden', timeout: 3000 }
          return
        }

        // Parsear puntos
        let puntosRaw = orden.puntos
        if (typeof puntosRaw === 'string') puntosRaw = JSON.parse(puntosRaw)
        if (!Array.isArray(puntosRaw)) return

        this.clearOrderPoints()
        this.showOrderPoints = true

        const map = this.Mymap && this.Mymap.map
        if (!map) return

        const bounds = new mapboxgl.LngLatBounds()
        puntosRaw.forEach((p, i) => {
          if (!p.coordenadas_entrega) return
          const parts = p.coordenadas_entrega.split(',')
          if (parts.length < 2) return
          const lng = parseFloat(parts[1])
          const lat = parseFloat(parts[0])
          if (isNaN(lat) || isNaN(lng)) return

          const el = document.createElement('div')
          el.style.cssText = 'background: #ff9800; color: white; padding: 6px 10px; border-radius: 50%; font-size: 12px; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3); width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;'
          el.textContent = String(i)

          const popup = new mapboxgl.Popup({ offset: 20, closeButton: true }).setHTML(
            '<div style="min-width: 180px;">' +
              '<div style="font-weight: 600; font-size: 14px;">Punto ' + (i + 1) + '</div>' +
              '<div style="font-size: 12px; margin-top: 4px;">' + (p.direccion || '-') + '</div>' +
              '<div style="font-size: 11px; color: #666; margin-top: 4px; font-family: monospace;">' + p.coordenadas_entrega + '</div>' +
            '</div>'
          )

          const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
            .setLngLat([lng, lat])
            .setPopup(popup)
            .addTo(map)
          this.orderPointMarkers.push(marker)
          bounds.extend([lng, lat])
        })

        // Ajustar el zoom para mostrar todos los puntos
        if (puntosRaw.length > 0 && !bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 80, maxZoom: 16, duration: 1000 })
        }

        this.isSnackbarVisible = true
        this.snackbar = {
          color: 'success',
          icon: 'success',
          title: 'Puntos cargados',
          text: puntosRaw.length + ' puntos de la orden #' + driver.orden_actual,
          timeout: 2000,
        }
      } catch (e) {
        console.error('[DriversMap] error cargando puntos de la orden:', e)
        this.isSnackbarVisible = true
        this.snackbar = { color: 'error', icon: 'error', title: 'Error', text: 'No se pudieron cargar los puntos', timeout: 3000 }
      }
    },
    itemRowClass(item) {
      const classes = ['clickable-row', 'driver-row']
      const estado = this.computeEstado(item)
      classes.push('row-' + estado.cls)
      if (this.selectedDriverId && Number(item.id) === Number(this.selectedDriverId)) {
        classes.push('row-selected')
      }
      return classes.join(' ')
    },
    estadoColorOf(cls) {
      if (cls === 'en-curso') return '#e3f2fd'
      if (cls === 'en-espera') return '#fff3e0'
      if (cls === 'en-proceso') return '#e1f5fe'
      if (cls === 'desconectado') return '#f5f5f5'
      return null
    },
    borderColorOf(cls) {
      if (cls === 'en-curso') return '#2196f3'
      if (cls === 'en-espera') return '#ff9800'
      if (cls === 'en-proceso') return '#03a9f4'
      if (cls === 'desconectado') return '#9e9e9e'
      return null
    },
    getRowStyle(item) {
      const cls = this.computeEstado(item).cls
      const bg = this.estadoColorOf(cls)
      const border = this.borderColorOf(cls)
      const isSelected = this.selectedDriverId && Number(item.id) === Number(this.selectedDriverId)
      let style = ''
      if (isSelected) {
        style += 'background-color: #bbdefb;'
      } else if (bg) {
        style += 'background-color: ' + bg + ';'
      }
      if (border) {
        style += 'border-left: 4px solid ' + border + ';'
      }
      if (cls === 'desconectado' && !isSelected) {
        style += 'color: #9e9e9e;'
      }
      return style
    },
    getCellStyle(item, align) {
      const cls = this.computeEstado(item).cls
      const isSelected = this.selectedDriverId && Number(item.id) === Number(this.selectedDriverId)
      let style = 'text-align: ' + (align || 'left') + ';'
      if (isSelected) {
        style += 'background-color: #bbdefb;'
      } else {
        const bg = this.estadoColorOf(cls)
        if (bg) style += 'background-color: ' + bg + ';'
      }
      return style
    },
    getSelectedDriverName() {
      if (!this.selectedDriverId) return ''
      const d = this.drivers.find(x => Number(x.id) === Number(this.selectedDriverId))
      return d ? d.nombre : ''
    },
    driverForOrder() {
      if (!this.orderInfo || !this.orderInfo.conductor_id) return null
      return this.drivers.find(x => Number(x.id) === Number(this.orderInfo.conductor_id)) || null
    },
    ordenEstadoLabel() {
      if (!this.orderInfo) return ''
      const e = Number(this.orderInfo.estado)
      if (e === -1) return 'Asignada (pendiente de aceptación)'
      if (e === 99) return 'Finalizada'
      if (e >= 0 && e < 99) return 'En curso (pto ' + e + ')'
      return 'Estado: ' + e
    },
    ordenEstadoColor() {
      if (!this.orderInfo) return 'grey'
      const e = Number(this.orderInfo.estado)
      if (e === -1) return 'orange'
      if (e === 99) return 'green'
      if (e >= 0 && e < 99) return 'blue'
      return 'grey'
    },
    goToOrder(ordenId) {
      if (!ordenId) return
      console.log('[DriversMap] navegando a orden', ordenId)
      this.$router.push('/apps/maporder/' + ordenId)
    },
    goToOrderMap(ordenId) {
      this.closeOrderDialog()
      this.goToOrder(ordenId)
    },
    async openOrderDialog(ordenId) {
      if (!ordenId) return
      this.orderDialogId = ordenId
      this.orderDialog = true
      this.orderLoading = true
      this.orderInfo = null
      this.orderPuntos = []
      try {
        const res = await this.$store.dispatch('get', { path: 'ordenes/getByIdWithPoints/' + ordenId })
        const orden = Array.isArray(res) ? res[0] : res
        if (!orden) {
          this.orderLoading = false
          return
        }
        this.orderInfo = orden
        // Leer puntos desde la tabla orden_puntos_mapa
        if (Array.isArray(orden.puntos_mapa)) {
          this.orderPuntos = orden.puntos_mapa.map((p, i) => ({
            id: p.id != null ? p.id : i,
            nombre: p.nombre || ('Punto ' + (i + 1)),
            direccion: p.direccion || '-',
            coordenadas_inicio: p.coordenadas_inicio || '-',
            coordenadas_entrega: p.coordenadas_entrega || '-',
            distancia_viaje: p.distancia_viaje || '-',
            tiempo_viaje: p.tiempo_viaje || '-',
            precio_x_distancia: p.precio_x_distancia || '-',
            estado: Number(p.estado) === 1,
            telefono: p.telefono || '-',
            descripcion: p.descripcion || '-',
            total: p.total != null ? Number(p.total) : null,
          }))
        } else if (typeof orden.puntos === 'string' || Array.isArray(orden.puntos)) {
          // Fallback: si la respuesta no trae puntos_mapa, intentar desde el JSON embebido
          try {
            let puntosRaw = orden.puntos
            if (typeof puntosRaw === 'string') puntosRaw = JSON.parse(puntosRaw)
            if (Array.isArray(puntosRaw)) {
              this.orderPuntos = puntosRaw.map((p, i) => ({
                id: p.id != null ? p.id : i,
                nombre: p.nombre || ('Punto ' + (i + 1)),
                direccion: p.direccion || '-',
                coordenadas_inicio: p.coordenadas_inicio || '-',
                coordenadas_entrega: p.coordenadas_entrega || '-',
                distancia_viaje: p.distancia_viaje || '-',
                tiempo_viaje: p.tiempo_viaje || '-',
                estado: !!p.estado,
              }))
            }
          } catch (e) {
            console.warn('[DriversMap] no se pudo parsear puntos embebidos:', e)
          }
        }
        this.orderLoading = false
      } catch (e) {
        console.error('[DriversMap] error cargando orden:', e)
        this.orderLoading = false
      }
    },
    closeOrderDialog() {
      this.orderDialog = false
      this.orderInfo = null
      this.orderPuntos = []
      this.orderDialogId = null
    },
    getSocketUrl() {
      const apiUrl = (this.$store && this.$store.state && this.$store.state.url) || ''
      // Convertir http://host:puerto/api/ -> http://host:puerto
      try {
        const u = new URL(apiUrl)
        return u.protocol + '//' + u.host
      } catch (e) {
        // fallback
        return apiUrl.replace(/\/api\/$/, '').replace(/\/$/, '')
      }
    },
    connectSocket() {
      if (this.socket) {
        try { this.socket.disconnect() } catch (e) {}
        this.socket = null
      }
      const socketUrl = this.getSocketUrl()
      console.log('[DriversMap] conectando socket a', socketUrl)
      this.socket = io(socketUrl, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
      })

      this.socket.on('connect', () => {
        console.log('[DriversMap] socket conectado', this.socket.id)
        this.socketConnected = true
        // El admin no necesita "register" específico porque el backend reemite
        // driver_location a la room cliente_<id>; como admin queremos
        // escuchar TODOS los driver_location, así que nos unimos a cada
        // room de cliente conocido. Una alternativa más limpia es que el
        // backend reemita también a una room "admin" general, pero por
        // ahora suscribimos por cliente_id.
        this.drivers.forEach(d => {
          if (d.id) {
            this.socket.emit('register', { usuario_id: d.id, rol: 'cliente' })
          }
        })
        this.isSnackbarVisible = true
        this.snackbar = {
          color: 'success',
          icon: 'success',
          title: 'Socket conectado',
          text: 'Recibiendo actualizaciones en tiempo real',
          timeout: 2000,
        }
      })

      this.socket.on('disconnect', () => {
        console.log('[DriversMap] socket desconectado')
        this.socketConnected = false
      })

      this.socket.on('connect_error', err => {
        console.error('[DriversMap] socket connect_error:', err)
      })

      this.socket.on('driver_location', data => {
        this.onDriverLocation(data)
      })
    },
    onDriverLocation(data) {
      try {
        if (!data || !data.conductor_id) return
        this.lastSocketUpdate = new Date()
        // Buscar el driver y actualizar su posición
        const idx = this.drivers.findIndex(d => Number(d.id) === Number(data.conductor_id))
        if (idx === -1) {
          // Si no lo conocemos, hacer un refresh completo para traerlo
          console.log('[DriversMap] driver_location de conductor_id=' + data.conductor_id + ' no conocido, refrescando')
          this.refresh(false)
          // suscribirse a su room
          if (this.socket && this.socket.connected) {
            this.socket.emit('register', { usuario_id: data.conductor_id, rol: 'cliente' })
          }
          return
        }
        // Actualizar la entrada del driver en this.drivers (Vue reactividad)
        const updated = { ...this.drivers[idx] }
        if (typeof data.lat === 'number') updated.lat_actual = data.lat
        if (typeof data.lng === 'number') updated.lng_actual = data.lng
        if (data.timestamp) updated.ult_actualizacion_coordenadas = data.timestamp
        // Reemplazar en el array
        const newDrivers = [...this.drivers]
        newDrivers[idx] = updated
        this.drivers = newDrivers
        // Si el driver está seleccionado, recentrar el mapa para que lo siga
        if (this.selectedDriverId && Number(this.selectedDriverId) === Number(data.conductor_id) && this.Mymap && this.Mymap.map) {
          this.Mymap.map.flyTo({
            center: [parseFloat(updated.lng_actual), parseFloat(updated.lat_actual)],
            zoom: 15,
            speed: 1.2,
          })
        }
        // Mover el marker imperativo
        if (this.mapMarkers[data.conductor_id]) {
          try {
            this.mapMarkers[data.conductor_id].setLngLat([parseFloat(updated.lng_actual), parseFloat(updated.lat_actual)])
          } catch (e) {}
        }
        console.log('[DriversMap] marker actualizado conductor_id=' + data.conductor_id + ' lat=' + updated.lat_actual + ' lng=' + updated.lng_actual)
      } catch (e) {
        console.error('[DriversMap] error onDriverLocation:', e)
      }
    },
  },
}
</script>

<style scoped>
.driver-marker {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
}
.driver-sin-ordenes {
  background-color: #4caf50;
}
.driver-en-espera {
  background-color: #ff9800;
}
.driver-en-proceso {
  background-color: #2196f3;
}
.driver-desconectado {
  background-color: #9e9e9e;
}
</style>
<style>
.clickable-rows .clickable-row {
  cursor: pointer;
}
.clickable-rows .clickable-row:hover {
  background-color: #f5f5f5 !important;
}
.clickable-rows .row-selected {
  background-color: #e3f2fd !important;
  font-weight: 600;
}
.clickable-rows .row-selected:hover {
  background-color: #bbdefb !important;
}
.clickable-rows tr {
  transition: background-color 0.15s;
}
.clickable-rows tr:hover td {
  background-color: #fafafa !important;
}
.clickable-rows tr.row-selected td {
  background-color: #bbdefb !important;
}
</style>
